import { useState, useRef, useEffect, KeyboardEvent } from 'react';

interface Props {
    onSend: (message: string) => void;
    disabled?: boolean;
}

export const ChatInput = ({ onSend, disabled }: Props) => {
    const [message, setMessage] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    }, [message]);

    // Re-focus cuando termina de estar disabled (respuesta completada)
    useEffect(() => {
        if (!disabled && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [disabled]);

    const handleSubmit = () => {
        if (message.trim() && !disabled) {
            onSend(message.trim());
            setMessage('');
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };

    return (
        <div className="tw-border-t tw-border-gray-200 tw-p-3 tw-bg-white tw-shrink-0">
            <div className="tw-flex tw-gap-2 tw-items-end">
                <textarea
                    ref={textareaRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe tu pregunta..."
                    disabled={disabled}
                    maxLength={2000}
                    rows={1}
                    autoFocus
                    className="tw-flex-1 tw-resize-none tw-border tw-border-gray-300 tw-rounded-xl tw-px-4 tw-py-2
                               tw-text-sm tw-outline-none focus:tw-border-primary focus:tw-ring-1 focus:tw-ring-primary
                               disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed
                               tw-max-h-[120px] tw-min-h-[40px]"
                />
                <button
                    onClick={handleSubmit}
                    disabled={disabled || !message.trim()}
                    className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-primary tw-text-white tw-flex tw-items-center tw-justify-center
                               tw-border-0 tw-cursor-pointer tw-transition-all
                               hover:tw-bg-primary/90 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed
                               tw-shrink-0"
                    aria-label="Enviar mensaje"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="tw-w-5 tw-h-5">
                        <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
                    </svg>
                </button>
            </div>
            <p className="tw-text-xs tw-text-gray-400 tw-mt-1 tw-text-center tw-m-0">
                Enter para enviar, Shift+Enter para nueva linea
            </p>
        </div>
    );
};

export default ChatInput;
